import { useLazyQuery, useQuery } from "@apollo/client";
import cn from "classnames";
import isArray from "lodash/isArray";
import mergeWith from "lodash/mergeWith";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { GET_PAGE, GET_PAGE_ADMIN, GET_ALL_WIDGETS } from "@api/queriesV2/cms";
import LoadingPage from "@components/cms/LoadingPage/LoadingPage";
import getBlockTemplate from "@components/cmsBlocks/getBlockTemplate";
// import RenderTemplate from "@components/cmsTemplates/RenderTemplate";
import {
  getDefaultPageBlocks,
  getAdminTemplatePage,
  getPageTypes,
  getTemplatePageData,
} from "@config/cms";
import { useIsMounted } from "@hooks/useIsMounted";
import { useStore } from "@hooks/useZustandProvider";
import { mergeDataBlockWithDefaultData } from "@utils/cms";
import { getSlugAndType } from "@utils/utils";

import s from "./CmsLayout.module.scss";

const RenderTemplate = dynamic(
  () => import("@components/cmsTemplates/RenderTemplate"),
  {
    ssr: <LoadingPage />,
  },
);

const CursorNotice = dynamic(
  () => import("@ui-kit/cms/CursorNotice/CursorNotice"),
  {
    ssr: false,
  },
);

const CmsPanel = dynamic(
  () => import("@components/cmsLayout/CmsPanel/CmsPanel"),
  {
    ssr: false,
  },
);

const PageSettings = dynamic(
  () => import("@components/cmsLayout/PageSettings/PageSettings"),
  {
    ssr: false,
  },
);

const General = dynamic(() => import("@components/cmsLayout/General/General"), {
  ssr: false,
});

const CreatePage = dynamic(
  () => import("@components/cmsLayout/CreatePage/CreatePage"),
  {
    ssr: false,
  },
);

const LeaveEditModeNotice = dynamic(
  () => import("@modals/cms/LeaveEditModeNotice/LeaveEditModeNotice"),
  {
    ssr: false,
  },
);

const RemoveLangNotice = dynamic(
  () => import("@modals/cms/RemoveLangNotice/RemoveLangNotice"),
  {
    ssr: false,
  },
);

const RemovePageNotice = dynamic(
  () => import("@modals/cms/RemovePageNotice/RemovePageNotice"),
  {
    ssr: false,
  },
);

const DublicatePageNotice = dynamic(
  () => import("@modals/cms/DublicatePageNotice/DublicatePageNotice"),
  {
    ssr: false,
  },
);

export const CmsLayout = ({ children }) => {
  const isMounted = useIsMounted();
  // const [shouldReload, setShouldReload] = useState(false);
  // const [isNotFound, setIsNotFound] = useState(false);
  const { locales, locale, query, asPath, ...router } = useRouter();
  const {
    addWidgetsToWidgetsList,
    changePage,
    toggleEditMode,
    updateModals,
    changePageLang,
    modals,
    cms,
    user,
    setIsPageNotFound,
  } = useStore();
  const {
    isOpenLeaveEditModeNoticeModal,
    isOpenRemoveLangNoticeModal,
    isOpenRemovePageNoticeModal,
    isOpenUploadImagesModal,
    isOpenDublicatePageNoticeModal,
  } = modals;
  const { isEditMode } = cms.uiOptions;
  const { isWidgetEditing, list } = cms.widget;
  const { lang, draft, isNewPage, hasChanges, isPageNotFound } = cms.page;

  // useEffect(() => {
  //   if (shouldReload && isMounted) {
  //     router.replace(asPath).then(() => {
  //       setShouldReload(false);
  //     });
  //   }
  // }, [shouldReload]);

  // useEffect(() => {
  //   if (!isEditMode) {
  //     return setShouldReload(true);
  //   }
  //   return setShouldReload(false);
  // }, [isEditMode]);

  const toggleLeaveEditModeNoticeModal = () => {
    updateModals({
      isOpenLeaveEditModeNoticeModal: !isOpenLeaveEditModeNoticeModal,
    });
  };

  const toggleRemoveLangNoticeModal = () => {
    updateModals({
      isOpenRemoveLangNoticeModal: !isOpenRemoveLangNoticeModal,
    });
  };

  const toggleRemovePageNoticeModal = () => {
    updateModals({
      isOpenRemovePageNoticeModal: !isOpenRemovePageNoticeModal,
    });
  };

  const toggleDublicatePageNoticeModal = () => {
    updateModals({
      isOpenDublicatePageNoticeModal: !isOpenDublicatePageNoticeModal,
    });
  };

  const { slug, type } = getSlugAndType(asPath);

  const isNewPageRoute = router.route === "/new-page";

  const isCorrectType = getPageTypes(true).includes(query.type);

  const { loading } = useQuery(GET_PAGE_ADMIN, {
    context: { clientName: "v2" },
    variables: {
      slug,
      type,
      language: locale,
    },
    // skip: !isEditMode || isNewPageRoute || hasChanges,
    skip: !isEditMode || isNewPageRoute,
    fetchPolicy: "no-cache",
    ssr: false,
    onCompleted: (data) => {
      try {
        const error = data.adminGetPage;

        if (error.ErrorCode) {
          const page = getAdminTemplatePage(locales, "regular");

          changePage({
            data: page,
            draft: page,
          });

          if (error.ErrorCode === "Page does not exist") {
            setIsPageNotFound(true);
            notFound();
          }
          return toast.error(`[${error.ErrorCode}]: ${error.ErrorReason}`);
        }

        setIsPageNotFound(false);

        const { data: languages, ...restPage } = data.adminGetPage;
        const newLanguages = {};

        locales.forEach((locale) => {
          if (languages[locale]) {
            const blocks = languages[locale].pageData.blocks.map((item) => {
              const { name, widgetId } = item;

              if (widgetId) {
                return item;
              }

              const block = mergeDataBlockWithDefaultData(item, name);

              return block;
            });

            return (newLanguages[locale] = {
              ...languages[locale],
              pageData: { blocks },
            });
          } else {
            newLanguages[locale] = getTemplatePageData();
          }
        });

        const page = {
          ...restPage,
          languages: newLanguages,
        };

        changePage({
          data: page,
          draft: page,
        });
      } catch (e) {
        console.log("[GET_PAGE_ADMIN] catch:", e);
      }
    },
    onError: (error) => {
      setIsPageNotFound(true);
      const page = getAdminTemplatePage(locales, "regular");

      console.log("onError ERROR", page);

      changePage({
        data: page,
        draft: page,
      });
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const [getAllWidgets] = useLazyQuery(GET_ALL_WIDGETS, {
    context: { clientName: "v2" },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const error = data.getAllWidgets;
      if (error.ErrorCode || error.ErrorReason) {
        return toast.error(`[${error.ErrorCode}]: ${error.ErrorReason}`);
      }

      const widgets = data.getAllWidgets.widgets;

      addWidgetsToWidgetsList(widgets);
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  const checkIsNewPage = () => {
    const isNewPage = getPageTypes(true).includes(query.type);

    changePage({
      isNewPage,
    });
  };

  useEffect(() => {
    checkIsNewPage();
    // router.events.on("routeChangeComplete", checkIsNewPage);

    // return () => {
    //   router.events.off("routeChangeComplete", checkIsNewPage);
    // };
  }, [query.type]);

  // useEffect(() => {
  //   if (isEditMode) {
  //     changePageLang(locale);
  //   }
  // }, [locale]);

  // useEffect(() => {
  //   // if (!isEditMode) {
  //   refetch();
  //   // }
  // }, [isEditMode]);

  useEffect(() => {
    const handleNewPage = async () => {
      if (isEditMode && isNewPage && query.type) {
        const blocks = getDefaultPageBlocks(query.type); // fix for new type of pages

        await new Promise((res) => {
          getAllWidgets();
          res();
        });

        console.log("blocks", blocks);

        const newBlocks = blocks.reduce((result, block) => {
          if (
            block.widgetId &&
            typeof block.widgetId === "number" &&
            list[block.widgetId]
          ) {
            const newBlock = {
              widgetId: block.widgetId,
              isShow: true,
              widgetType: list[block.widgetId]?.type,
            };

            const newResult = [...result, newBlock];
            return newResult;
          }

          const newResult = [...result, block];

          return newResult;
        }, []);

        const newPage = getAdminTemplatePage(locales, query.type, newBlocks);

        changePage({
          data: newPage,
          draft: newPage,
        });
      }
    };

    handleNewPage();
  }, [isEditMode, isNewPage, query.type]);

  useEffect(() => {
    if (isEditMode) {
      if (!isCorrectType && isNewPageRoute) {
        router.replace(`/new-page/?type=${getPageTypes().regular}`);
      }
    }
  }, [isCorrectType]);

  return (
    <>
      {isOpenLeaveEditModeNoticeModal && (
        <LeaveEditModeNotice
          isOpen={isOpenLeaveEditModeNoticeModal}
          toggleModal={toggleLeaveEditModeNoticeModal}
        />
      )}
      {isOpenRemoveLangNoticeModal && (
        <RemoveLangNotice
          isOpen={isOpenRemoveLangNoticeModal}
          toggleModal={toggleRemoveLangNoticeModal}
        />
      )}
      {isOpenRemovePageNoticeModal && (
        <RemovePageNotice
          isOpen={isOpenRemovePageNoticeModal}
          toggleModal={toggleRemovePageNoticeModal}
        />
      )}
      {isOpenDublicatePageNoticeModal && (
        <DublicatePageNotice
          isOpen={isOpenDublicatePageNoticeModal}
          toggleModal={toggleDublicatePageNoticeModal}
        />
      )}

      {/* {!loading && !!draft && !!draft?.[lang] && ( */}
      {isEditMode && !!draft && !!draft?.languages && (
        <>
          <CursorNotice />
          <CmsPanel />
          <PageSettings />
          <General />
          <CreatePage />
        </>
      )}
      <div
        className={cn(
          isMounted && isEditMode && s.editMode,
          isWidgetEditing && s.isWidgetEditing,
        )}
      >
        {isEditMode && !!draft?.languages && !isPageNotFound ? (
          <RenderTemplate loading={loading} notFound={isPageNotFound} />
        ) : (
          children
        )}
      </div>
    </>
  );
};
